import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { catchError, finalize, throwError, EMPTY } from 'rxjs';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { LoaderService } from '../services/loader.service';
import { AuthService } from '../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';

// optional: local flag to prevent multiple logouts in parallel
let isLoggingOut = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  const toastr = inject(ToastrService);
  const loaderService = inject(LoaderService);
  const authService = inject(AuthService);

  // Safe token read (browser only)
  let token: string | undefined;
  if (isBrowser) {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) token = JSON.parse(raw)?.token;
    } catch {
      token = undefined;
    }
  }

  // Merge with existing headers
  let headers = req.headers
    .set('ssuite-userId', '1')
    .set('ssuite-userAgent', '1')
    .set('ssuite-ip', '1');

  if (environment.workspaceId != null) {
    headers = headers.set('ssuite-ctxId', String(environment.workspaceId));
  }

  // Don’t attach token to login call
  const isLogin = req.url.includes('/sharesuitesso/login');
  if (!isLogin && token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const cloned = req.clone({ headers });

  if (isBrowser) loaderService.showLoader();

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      // --- 401: Unauthorized → logout (unless it's the login call itself) ---
      if (error.status === HttpStatusCode.Unauthorized) {
        if (isLogin) {
          // If login returns 401, show error instead of logging out
          if (isBrowser) toastr.error(error.error?.description ?? 'Invalid credentials');
          return throwError(() => error);
        }

        if (isBrowser && !isLoggingOut) {
          isLoggingOut = true;
          // Call your central logout (clear storage, navigate to /login, etc.)
          authService.handleLogout();
          // Reset flag after a short tick; alternatively reset in handleLogout() after navigate
          setTimeout(() => (isLoggingOut = false), 1000);
        }

        // Choose ONE of the returns below:

        // A) Logout & rethrow (lets feature code see the error)
        return throwError(() => error);

        // B) Logout & swallow (prevents duplicate global toasts)
        // return EMPTY;
      }

      // --- 403: Forbidden → logout (your existing behavior) ---
      if (error.status === HttpStatusCode.Forbidden) {
        if (isBrowser && !isLoggingOut) {
          isLoggingOut = true;
          authService.handleLogout();
          setTimeout(() => (isLoggingOut = false), 1000);
        }
        return throwError(() => error);
      }

      // --- Other errors ---
      if (isLogin) {
        if (isBrowser) toastr.error(error.error?.description ?? 'An unexpected error occurred');
      } else if (error.status !== HttpStatusCode.NotFound) {
        if (isBrowser) toastr.error(error.error?.description ?? 'An unexpected error occurred');
      }

      return throwError(() => error);
    }),
    finalize(() => {
      if (isBrowser) loaderService.hideLoader();
    })
  );
}